import { DateTime } from 'luxon';

import { Button } from '../Input2';
import { Arrows, General } from '../StrokeIcons';
import { WaveSpinner } from '../WaveSpinner';

type ResponseAction =
  | 'email_received'
  | 'inmail_received'
  | 'inmail_declined'
  | 'message_received'
  | 'document_viewed';

const allowedResponseTypes = new Set<ResponseAction>([
  'email_received',
  'inmail_received',
  'inmail_declined',
  'message_received',
  'document_viewed',
]);

const containerStyle = {
  background:
    'conic-gradient(from -45deg at bottom, var(--background-color) 90deg, var(--card-background) 0) 50%/10px 100%',
} as const;
const pausedStyle = {
  background:
    'conic-gradient(from -45deg at bottom, var(--background-color) 90deg, var(--needs-action) 0) 50%/10px 100%',
} as const;

export interface ResponseSectionProps {
  contactFirstName?: string;
  cadenceState?: string | null;
  responseAction: string;
  responseDate: Date;
  responseSubject?: string | null;
  responseBody?: string | null;
  isLoading?: boolean;
  onRespond?: () => void;
  onDismissResponse?: () => void;
}

export const ResponseSection = ({
  contactFirstName,
  cadenceState,
  responseAction,
  responseDate,
  responseSubject,
  responseBody,
  isLoading,
  onRespond,
  onDismissResponse,
}: ResponseSectionProps) => {
  if (!allowedResponseTypes.has(responseAction as ResponseAction)) return null;

  const timeAgo = DateTime.fromJSDate(responseDate).toRelative();

  const message =
    responseAction === 'document_viewed'
      ? `Respond to ${contactFirstName} Viewing ${responseSubject}`
      : `Respond to ${contactFirstName} "${responseBody}"`;

  return (
    <div
      className="flex flex-col gap-1.5 px-[15px] pt-5 pb-[30px] [--background-color:#e6e2f1] dark:[--background-color:#474779]"
      style={cadenceState === 'paused' ? pausedStyle : containerStyle}>
      {onRespond && (
        <Button
          onClick={onRespond}
          icon={isLoading ? <WaveSpinner /> : <Arrows.ReverseLeft />}
          className="text-left justify-start [&>span]:flex [&>span]:grow [&>span]:gap-1"
          color="aurora">
          <span className="min-w-0 truncate">{message}</span>
          <span className="opacity-40 text-xs shrink-0 ml-1">{timeAgo}</span>
        </Button>
      )}
      {onDismissResponse && (
        <Button
          icon={isLoading ? <WaveSpinner /> : <General.XCircle />}
          onClick={onDismissResponse}
          className="text-left justify-start [&_strong]:font-semibold">
          <strong>Dismiss</strong>&nbsp;until you receive another
        </Button>
      )}
    </div>
  );
};
